import React, { useState, useEffect, useContext } from 'react';
import { 
  UserIcon,
  CalendarIcon,
  CakeIcon,
  BriefcaseIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../../contexts/AuthContext';
import calendarApi from '../../services/calendarApi';
import api from '../../utils/api';

const UserProfile = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    birthday: '',
    hire_date: ''
  });
  const [originalData, setOriginalData] = useState({
    birthday: '',
    hire_date: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    // Check if there are changes
    const changed = profileData.birthday !== originalData.birthday || 
                   profileData.hire_date !== originalData.hire_date;
    setHasChanges(changed);
  }, [profileData, originalData]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      // Use the existing api utility instead of direct fetch
      const response = await api.get('/auth/me');
      
      const userData = {
        birthday: response.data.user.birthday || '',
        hire_date: response.data.user.hire_date || ''
      };
      
      setProfileData(userData);
      setOriginalData(userData);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Only send fields that have changed
      const updates = {};
      if (profileData.birthday !== originalData.birthday) {
        updates.birthday = profileData.birthday || null;
      }
      if (profileData.hire_date !== originalData.hire_date) {
        updates.hire_date = profileData.hire_date || null;
      }

      if (Object.keys(updates).length === 0) {
        toast.error('No changes to save');
        return;
      }

      await calendarApi.updateProfile(updates);
      toast.success('Profile updated successfully');
      
      // Update original data to reflect saved state
      setOriginalData(profileData);
      setHasChanges(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData(originalData);
    setHasChanges(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      // Handle the date string directly without creating a Date object
      // This completely avoids any timezone conversion issues
      const [year, month, day] = dateString.split('-');
      
      // Validate the components
      if (!year || !month || !day) {
        return 'Invalid date';
      }
      
      // Format manually using just the string components
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      const monthIndex = parseInt(month) - 1;
      const dayNum = parseInt(day);
      
      if (monthIndex < 0 || monthIndex > 11 || dayNum < 1 || dayNum > 31) {
        return 'Invalid date';
      }
      
      return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
    } catch (e) {
      return 'Invalid date';
    }
  };

  if (loading && !profileData.birthday && !profileData.hire_date) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
              <p className="mt-1 text-gray-600">
                Update your personal information for company calendar events
              </p>
            </div>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {/* User Info Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              {currentUser?.picture ? (
                <img
                  src={currentUser.picture}
                  alt={currentUser.name}
                  className="h-16 w-16 rounded-full"
                />
              ) : (
                <div className="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center">
                  <UserIcon className="h-8 w-8 text-gray-600" />
                </div>
              )}
              <div className="ml-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {currentUser?.name || 'User'}
                </h2>
                <p className="text-gray-600">{currentUser?.email}</p>
                <p className="text-sm text-gray-500 capitalize">
                  {currentUser?.role_type || 'User'}
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="px-6 py-6">
            <div className="space-y-6">
              {/* Birthday Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CakeIcon className="h-5 w-5 inline mr-2 text-pink-500" />
                  Birthday
                </label>
                
                {/* Current Value Display */}
                {originalData.birthday && (
                  <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Current Birthday:</span>
                        <span className="ml-2 text-base font-semibold text-gray-900">
                          {formatDate(originalData.birthday)}
                        </span>
                      </div>
                      <CakeIcon className="h-5 w-5 text-pink-400" />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 mb-1">
                    {originalData.birthday ? 'Update Birthday:' : 'Set Birthday:'}
                  </label>
                  <input
                    type="date"
                    value={profileData.birthday}
                    onChange={(e) => handleInputChange('birthday', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Your birthday will appear on the company calendar for team celebrations
                </p>
              </div>

              {/* Hire Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <BriefcaseIcon className="h-5 w-5 inline mr-2 text-blue-500" />
                  Hire Date
                </label>
                
                {/* Current Value Display */}
                {originalData.hire_date && (
                  <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Current Hire Date:</span>
                        <span className="ml-2 text-base font-semibold text-gray-900">
                          {formatDate(originalData.hire_date)}
                        </span>
                      </div>
                      <BriefcaseIcon className="h-5 w-5 text-blue-400" />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 mb-1">
                    {originalData.hire_date ? 'Update Hire Date:' : 'Set Hire Date:'}
                  </label>
                  <input
                    type="date"
                    value={profileData.hire_date}
                    onChange={(e) => handleInputChange('hire_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Your hire date is used to calculate work anniversaries on the company calendar
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {hasChanges && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center disabled:opacity-50"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckIcon className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Information Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <CalendarIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">
                About Calendar Information
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Your birthday will be displayed on the company calendar for team celebrations</li>
                  <li>Work anniversaries are calculated automatically from your hire date</li>
                  <li>This information is visible to all team members on the company calendar</li>
                  <li>You can update this information at any time</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 